import { validateBlockDefinition } from "./definition-validation";
import type {
    AnyControl,
    BlockDefinition,
    BlockSettingConfig,
    BlockStyles,
    FilterItem,
    InferBlockStylesValues,
    SimpleVisibilityCondition,
    Style,
    StyleGroup,
    UserAccountsDefinition,
} from "./types";
import { get } from "./utils";
import { SortOrderEnum } from "./types";

/**
 * Create a "prototype" block definition for a block.
 * @example
 * // blocks/my-category/my-block/my-block.definition.ts
 * export default blockDefinition({
 *    id: "3094FF9C-0340-4C79-8079-EED0166E9C0B",
 *    name: "my-block",
 *    version: "1.0.0",
 *    type: "dynamic",
 *    ...blockDefinition.defaults,
 *    collection: {
 *       ...blockDefinition.collectionDefaults,
 *       dataSource: {
 *         ...blockDefinition.collectionDataSourceDefaults,
 *       }
 *       /// more fields specific to this block
 *    }
 *    // more fields specific to this block
 * })
 */
export const blockDefinition = (definition: BlockDefinition) => {
    validateBlockDefinition(definition);

    return {
        ...definition,
        hrid: definition.hrid || "",
    };
};
blockDefinition.defaults = {
    hrid: "",
    device_visibility: ["laptop", "tablet", "mobile"],
    enabled: true,
    visibility: {
        user_group: {
            predefined_user_group_type: "ALL_USERS",
            custom_user_group_ids: [],
            enabled_predefined_user_group_types: ["ALL_USERS", "LOGGED_IN_USERS", "NON_LOGGED_IN_USERS"],
        },
        devices: ["laptop", "tablet", "mobile"],
    },
} satisfies Partial<BlockDefinition>;

blockDefinition.collectionDefaults = {
    dataSources: [],
    condition: {
        expressions: [],
        logicalOperator: "AND",
    },
    noMatchStrategy: "EMPTY_STATE",
    sortOptions: [],
    searchOptions: {
        fields: [],
    },
    actions: [],
} satisfies Partial<BlockDefinition["collection"]>;
blockDefinition.collectionDataSourceDefaults = {
    type: null,
    airtable: {
        url: "",
        baseId: "",
        tableName: "",
        viewName: "",
    },
    softr: {
        applicationId: "",
    },
    isComplete: false,
} as const satisfies Partial<BlockDefinition["collection"]["dataSource"]>;

export const userAccountsBlockDefinition = (definition: UserAccountsDefinition) => {
    validateBlockDefinition(definition);

    return {
        ...definition,
        hrid: definition.hrid || "",
    };
};

type GroupConfig = Omit<StyleGroup, "type" | "styles">;

/**
 * @example
 * group("Block", control("block.backgroundColor"), control("block.paddingTop"));
 * group({ label: "Table", expanded: true }, control("table.backgroundColor"), control("table.paddingTop"));
 */
export function group(config: GroupConfig | string, ...styles: Style[]): StyleGroup;
/**
 * @deprecated Prefer to use the overload with the `config` object as the first argument because it
 * leads to less nested code.
 */
export function group(styles: Style[], config: GroupConfig): StyleGroup;
export function group(
    first: Style[] | GroupConfig | string,
    second: GroupConfig | Style,
    ...rest: Style[]
): StyleGroup {
    let styles: Style[];
    let config: GroupConfig;
    if (Array.isArray(first)) {
        // Overload with the children first
        if (rest.length > 0) {
            throw new Error("Unexpected third argument");
        }
        styles = first;
        config = second as GroupConfig;
    } else {
        styles = second ? [second as Style, ...rest] : [...rest];
        if (typeof first === "string") {
            config = { label: first };
        } else {
            config = first;
        }
    }
    return {
        type: "group",
        styles,
        expanded: false,
        ...config,
    };
}

type ResolvePropertyPath<T, Path> = Path extends keyof T
    ? T[Path]
    : Path extends `${infer Key}.${infer Rest}`
      ? Key extends keyof T
          ? ResolvePropertyPath<T[Key], Rest>
          : never
      : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]];

type Join<K, P> = K extends string | number
    ? P extends string | number
        ? `${K}${"" extends P ? "" : "."}${P}`
        : never
    : never;

/**
 * https://stackoverflow.com/questions/58434389/typescript-deep-keyof-of-a-nested-object/58436959#58436959
 */
export type Leaves<T, TObject, D extends number = 10> = [D] extends [never]
    ? never
    : T extends TObject
      ? { [K in keyof T]-?: Join<K, Leaves<T[K], TObject, Prev[D]>> }[keyof T]
      : "";

type PathsOf<TBlockStyles> = Leaves<TBlockStyles, BlockStyles, 3>;

type ControlFunction<TBlockStyles extends BlockStyles> = <
    TPath extends PathsOf<TBlockStyles>,
    const VisibilityPath extends PathsOf<TBlockStyles> = never,
>(
    path: TPath,
    config?: Omit<BlockSettingConfig, "visible"> & {
        visible?: {
            key: VisibilityPath;
            operator: SimpleVisibilityCondition["operator"];
            value: ResolvePropertyPath<InferBlockStylesValues<TBlockStyles>, VisibilityPath>;
        };
    },
) => ResolvePropertyPath<TBlockStyles, TPath>;

export const createControl = <TBlockStyles extends BlockStyles>(
    styles: TBlockStyles,
): ControlFunction<TBlockStyles> => {
    return (path, config = {}) => {
        const control = get(styles, path) as AnyControl;
        return {
            ...control,
            key: path,
            ...config,
        } as never;
    };
};

export const filterItem = (label = "Filter by", values = "Tag1; Tag2; Tag3; Tag4; Tag5"): FilterItem => {
    return {
        label: { value: label },
        mappedTo: "",
        colorPalette: "NONE",
        multiselect: false,
        sort: SortOrderEnum.ASC,
        syncWithDatasource: false,
        values,
        condition: {
            enabled: false,
            logicalOperator: "AND",
            expressions: [],
        },
        // deprecated properties
        showAll: false,
        tagPerLine: false,
    };
};

export const filtersMobileView = () => ({
    applyButton: {
        text: "Apply",
    },
    removeButton: {
        text: "Clear all",
    },
    searchInput: {
        placeholder: "Search",
    },
});
